import { Direction } from "../movement/step.ts";
import type { ControlKind, Effect } from "../rules/types.ts";

/**
 * What effects forbid and how control diminishes (ADR-079 decisions 4 and 5), shared by the map server, which decides,
 * and the page, which predicts its own movement with the limits the server confirmed. Pure and allocation-free.
 */

/**
 * The limits an effect may set, as bits: moving, and acting (using an ability).
 */
export const EffectLimit = { Move: 1, Act: 2 } as const;
export type EffectLimit = (typeof EffectLimit)[keyof typeof EffectLimit];

/**
 * What each crowd-control category forbids (Bible 5.5): a stun forbids moving and acting.
 */
export const CONTROL_LIMITS: Readonly<Record<ControlKind, number>> = Object.freeze({ stun: EffectLimit.Move | EffectLimit.Act });

/**
 * The share of its duration each application in a diminishing-returns window lasts, in whole percents: the first,
 * the second and the third (Bible 5.5); every later one within the window lasts the last step, a quarter, and none is
 * refused (ADR-079's amendment of 2026-10-07).
 */
export const DIMINISHING_PERCENTS: readonly number[] = Object.freeze([100, 50, 25]);

/**
 * The limits one effect sets.
 *
 * @param effect The effect.
 * @returns Its `EffectLimit` bits, 0 for an effect with no control.
 */
export function limitsOfEffect(effect: Pick<Effect, "control">): number {
  return effect.control === null ? 0 : CONTROL_LIMITS[effect.control];
}

/**
 * The input the shared step moves by under a set of limits: none while moving is forbidden, so a stunned character
 * neither moves nor turns, and the input itself otherwise.
 *
 * @param limits The `EffectLimit` bits in force.
 * @param dir The input's direction.
 * @returns The direction to step by.
 */
export function directionUnder(limits: number, dir: Direction): Direction {
  return (limits & EffectLimit.Move) !== 0 ? Direction.None : dir;
}

/**
 * How long a controlling application lasts after others already landed in its window.
 *
 * @param durationTicks The effect's duration.
 * @param landed Applications of its category already landed in the window: 0, 1, 2 or more.
 * @returns The ticks, rounded down and at least 1.
 * @example
 * diminishedTicks(20, 2);
 * // 5: the third stun lasts a quarter
 * diminishedTicks(20, 9);
 * // 5: and so does the tenth
 */
export function diminishedTicks(durationTicks: number, landed: number): number {
  const percent = DIMINISHING_PERCENTS[Math.min(landed, DIMINISHING_PERCENTS.length - 1)] as number;
  return Math.max(1, Math.floor((durationTicks * percent) / 100));
}
